import { ApolloError } from "@apollo/client";
import { useDeleteFileMutation } from "apollo/hooks/mutations";
import {
  FILE_DELETION_TYPES,
  FILE_TABLE_ACTION_SYNC_VERSION,
  FILE_TABLE_ACTION_SYNC_VERSION_HIS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { useDeleteExtractedFile } from "core/model/api/endpoints/extractDocs/hooks/extract/delete";
import { getError } from "core/model/utils/errors";
import { FileParams, Filev2, QueryProgress } from "core/types";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useTranslations } from "translations";
import { FileFeatureData } from "./useFilesFeature";

export function useDeleteFile({
  action,
  params,
  setAction,
  setProgress,
}: {
  action: GeneralListAction<Filev2> | undefined;
  params: FileParams;
  setAction: SetAction<FileFeatureData>;
  setProgress: Dispatch<SetStateAction<QueryProgress>>;
}) {
  const translations = useTranslations();
  const toast = useToast();
  const { deleteExtract } = useDeleteExtractedFile();

  const fileDeletionType =
    action?.data && "fileDeletionType" in action.data
      ? action.data.fileDeletionType
      : undefined;

  const onError = useCallback((err: ApolloError | Error) => {
    toast({ color: "danger", message: translations.fileSection.toastError });
    console.log(err);
    setProgress(QUERY_PROGRESS_FAILED);
  }, []);

  const [deleteFile, deleteFileProgress] = useDeleteFileMutation({
    onCompleted: () => {
      if (
        ![
          FILE_TABLE_ACTION_SYNC_VERSION,
          FILE_TABLE_ACTION_SYNC_VERSION_HIS,
        ].includes(action?.type as any)
      ) {
        toast({
          color: "success",
          message: translations.fileSection.fileDeleteModal.toastSuccess,
        });
        setAction(undefined);
        setProgress(QUERY_PROGRESS_SUCCEED);
      }
    },
    onError,
  });

  const handleDeleteFile = useCallback(
    async (id?: number) => {
      setProgress(QUERY_PROGRESS_PENDING);
      const fileId = id || action?.data.id;
      if (!fileId) {
        onError(new Error("File ID is required"));
        return;
      }

      if (fileDeletionType === FILE_DELETION_TYPES.EXTRACTED_ONLY) {
        try {
          await deleteExtract(fileId);
          toast({
            color: "success",
            message: translations.fileSection.fileDeleteModal.toastSuccess,
          });
          setAction(undefined);
          setProgress(QUERY_PROGRESS_SUCCEED);
          return;
        } catch (err) {
          const error = getError(err);
          console.error(
            `Extracted data deletion failed: ${error.message}`,
            err,
          );

          toast({
            color: "danger",
            message: translations.general.status.error,
          });
        }
      } else {
        return deleteFile(undefined, { id: fileId, params });
      }
    },
    [action?.data.id, onError, params, deleteFile, setProgress],
  );

  return {
    deleteFileProgress,
    handleDeleteFile,
  };
}
